// The one way a SIMSM menu puts the keyboard focus inside a freshly opened
// `mud-menu` (059 V-093, V-094).
//
// mud-menu owns the roving focus (arrows, Home, End, Escape) but not the first
// landing, and it cannot take focus before it has rendered: until then its rows
// are light-DOM children of a shadow host that has no `<slot>` yet, so they are
// not rendered and `focus()` on one is a silent no-op. The effect that opens a
// menu runs in the same commit that mounts it, which is before that point — the
// focus stayed on the trigger, the arrows went nowhere and Escape never reached
// the menu.
//
// The custom-elements build of MUD has no `componentOnReady()` to await, so the
// readiness signal is the one Stencil itself leaves on the host: the `hydrated`
// class, added when the component has rendered and its children have loaded,
// immediately before `componentDidLoad` (where mud-menu resets its roving
// tabindex). The row is then focused through MUD's own `setFocus()` method,
// which also waits for the row's lazy instance.

type MenuItemElement = HTMLElement & {
  disabled?: boolean;
  value?: string;
  setFocus?: () => Promise<void>;
};

export interface FocusMenuItemOptions {
  /** The `mud-menu` that just opened, or the element holding several of them. */
  menu: HTMLElement | null;
  /** Which row takes the focus; defaults to the first enabled one. */
  pick?: (items: MenuItemElement[]) => MenuItemElement | undefined;
}

function menusIn(scope: HTMLElement): HTMLElement[] {
  return scope.matches('mud-menu')
    ? [scope]
    : Array.from(scope.querySelectorAll<HTMLElement>('mud-menu'));
}

function allHydrated(scope: HTMLElement): boolean {
  return menusIn(scope).every((menu) => menu.classList.contains('hydrated'));
}

/**
 * Focus a row of `menu` as soon as it can take it, or the (first) menu itself
 * when it has no enabled row. Returns a cancel function for
 * the effect cleanup: a menu that closes first must not steal the focus back.
 */
export function focusMenuItemWhenReady({ menu, pick }: FocusMenuItemOptions): () => void {
  if (!menu) return () => undefined;
  let cancelled = false;
  let observer: MutationObserver | null = null;
  const scope = menu;

  function land(): void {
    if (cancelled) return;
    // `disabled` is read as the property: the attribute MUD reflects it to
    // arrives a render later.
    const items = Array.from(scope.querySelectorAll<MenuItemElement>('mud-menu-item'));
    const enabled = items.filter((item) => item.disabled !== true);
    const target = (pick ? pick(enabled) : undefined) ?? enabled[0];
    if (!target) {
      // Every row is disabled. The menu itself takes the focus, so Escape still
      // reaches mud-menu and Tab still reaches the caller's key handler: left on
      // the trigger, neither did, and the menu could not be closed by keyboard.
      const first = menusIn(scope)[0];
      if (!first) return;
      if (!first.hasAttribute('tabindex')) first.tabIndex = -1;
      first.focus({ preventScroll: true });
      return;
    }
    // Roving tabindex: the row that takes the focus is the one tab stop of the
    // group, which is the value mud-menu writes for the first row on its own.
    for (const sibling of enabled.filter((item) => item.parentElement === target.parentElement)) {
      sibling.tabIndex = sibling === target ? 0 : -1;
    }
    if (typeof target.setFocus === 'function') {
      void target.setFocus().then(() => {
        if (!cancelled && document.activeElement !== target) target.focus({ preventScroll: true });
      });
    } else {
      target.focus({ preventScroll: true });
    }
  }

  if (allHydrated(scope)) {
    land();
  } else {
    observer = new MutationObserver(() => {
      if (!allHydrated(scope)) return;
      observer?.disconnect();
      observer = null;
      land();
    });
    for (const each of menusIn(scope)) {
      observer.observe(each, { attributes: true, attributeFilter: ['class'] });
    }
  }

  return () => {
    cancelled = true;
    observer?.disconnect();
    observer = null;
  };
}
